import React, { useState, useEffect } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { theme } from '../config/theme';
import { instructors } from '../config/instructors';
import Footer from '../components/Footer';
import BackButton from '../components/BackButton';
import { useBackButton } from '../contexts/BackButtonContext';

const PageContainer = styled.div`
  min-height: 100vh;
  background-color: ${theme.colors.primary.background};
  padding-top: 128px; /* Correct header height */
  box-sizing: border-box;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    padding-top: 96px; /* Correct mobile navigation height */
  }
`;

const InstructorContainer = styled(motion.section)`
  padding: ${theme.spacing.sectionPadding.vertical} ${theme.spacing.sectionPadding.horizontal};
  background-color: ${theme.colors.primary.background};
  position: relative;
  overflow: hidden;
  max-width: 1000px;
  margin: 0 auto;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    padding-top: ${theme.spacing.md};
  }
`;

const InstructorContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 400px;
  gap: 80px;
  align-items: start;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

const InstructorInfo = styled(motion.div)`
  display: flex;
  flex-direction: column;
`;

const InstructorPhotoContainer = styled(motion.div)`
  @media (max-width: ${theme.breakpoints.tablet}) {
    order: -1;
  }
`;

const InstructorPhoto = styled.img`
  width: 100%;
  height: 500px;
  object-fit: cover;
  filter: grayscale(20%) contrast(1.1);

  @media (max-width: ${theme.breakpoints.tablet}) {
    height: 300px;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    height: 250px;
  }
`;

const InstructorName = styled(motion.h1)`
  font-family: ${theme.typography.primary.fontFamily};
  font-size: ${theme.typography.primary.largeHeading.fontSize};
  font-weight: ${theme.typography.primary.largeHeading.fontWeight};
  color: ${theme.colors.primary.text};
  margin-bottom: 16px;
  line-height: 1.1;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    font-size: 2.5rem;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 2rem;
  }
`;

const InstructorTitle = styled(motion.p)`
  font-family: ${theme.typography.secondary.fontFamily};
  font-size: 0.9rem;
  font-weight: 500;
  color: ${theme.colors.primary.text};
  margin-bottom: 24px;
  text-transform: uppercase;
  letter-spacing: 0.15em;
  opacity: 0.7;
`;

const InstructorBlurb = styled(motion.p)`
  font-family: ${theme.typography.secondary.fontFamily};
  font-size: 1.1rem;
  line-height: 1.7;
  color: ${theme.colors.primary.text};
  margin-bottom: 40px;
  max-width: 500px;
`;

const CollapsibleSections = styled.div`
  margin-top: 40px;
  pointer-events: auto;
`;

const CollapsibleSection = styled.div`
  margin-bottom: ${theme.spacing.md};
`;

const CollapsibleHeader = styled.button`
  width: 100%;
  background: none;
  border: none;
  padding: 20px 0;
  text-align: left;
  font-family: ${theme.typography.secondary.fontFamily};
  font-size: 0.9rem;
  font-weight: 500;
  color: ${theme.colors.primary.text};
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid ${theme.colors.primary.ui_dark};
  transition: ${theme.transitions.default};
  text-transform: uppercase;
  letter-spacing: 0.1em;
  opacity: 0.8;
  pointer-events: auto;
  position: relative;
  z-index: 1;

  &:hover {
    opacity: 1;
  }
`;

const CollapsibleIcon = styled.span`
  font-size: 0.8rem;
  transition: transform 0.3s ease;
  transform: ${props => props.isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
`;

const CollapsibleContent = styled(motion.div)`
  padding: ${theme.spacing.md} 0;
  font-family: ${theme.typography.secondary.fontFamily};
  font-size: 0.9rem;
  line-height: 1.6;
  color: ${theme.colors.primary.text};
  white-space: pre-line;
`;


const InstructorPage = () => {
  const { slug } = useParams();
  const [openSections, setOpenSections] = useState({});
  const { setBackButton, clearBackButton } = useBackButton();

  const instructor = instructors.find(inst => inst.slug === slug);

  // Set back button info and scroll to top when component mounts and when slug changes
  useEffect(() => {
    // Set back button in context for mobile navigation
    setBackButton("← Back to Our Instructors", "/#team");
    
    // Use setTimeout to ensure the page has rendered
    const timer = setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'instant' });
    }, 50);
    
    return () => {
      clearTimeout(timer);
      clearBackButton();
    };
  }, [slug, setBackButton, clearBackButton]);

  if (!instructor) {
    return <Navigate to="/about" replace />;
  }

  const toggleSection = (sectionKey) => {
    setOpenSections(prev => ({
      ...prev,
      [sectionKey]: !prev[sectionKey]
    }));
  };

  const containerVariants = {
    hidden: { opacity: 0, y: theme.animations.moveMedium },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: theme.animations.fast,
        ease: 'easeOut',
        staggerChildren: theme.animations.staggerFast,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: theme.animations.moveSmall },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: theme.animations.fast,
        ease: 'easeOut',
      },
    },
  };

  const photoVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: theme.animations.fast,
        ease: 'easeOut',
        delay: theme.animations.staggerFast,
      },
    },
  };

  return (
    <PageContainer>
      <BackButton 
        text="← Back to Our Instructors" 
        to="/#team"
      />
      
      <InstructorContainer
        initial="hidden"
        animate="visible"
        variants={containerVariants}
      >
        
        <InstructorContent>
          <InstructorInfo>
            <InstructorName variants={itemVariants}>
              {instructor.name}
            </InstructorName>
            <InstructorTitle variants={itemVariants}>
              {instructor.title}
            </InstructorTitle>
            <InstructorBlurb variants={itemVariants}>
              {instructor.blurb}
            </InstructorBlurb>
            
            <CollapsibleSections>
              {instructor.sections.map((section, index) => (
                <CollapsibleSection key={index}>
                  <CollapsibleHeader 
                    onClick={() => toggleSection(`${instructor.name}-${index}`)}
                  >
                    {section.title}
                    <CollapsibleIcon isOpen={openSections[`${instructor.name}-${index}`]}>
                      ▼
                    </CollapsibleIcon>
                  </CollapsibleHeader>
                  <AnimatePresence>
                    {openSections[`${instructor.name}-${index}`] && (
                      <CollapsibleContent
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.4, ease: "easeInOut" }}
                      >
                        <div dangerouslySetInnerHTML={{ __html: section.content }} />
                      </CollapsibleContent>
                    )}
                  </AnimatePresence>
                </CollapsibleSection>
              ))}
            </CollapsibleSections>
          </InstructorInfo>
          
          <InstructorPhotoContainer>
            <InstructorPhoto 
              variants={photoVariants}
              src={instructor.photo} 
              alt={instructor.name}
            />
          </InstructorPhotoContainer>
        </InstructorContent>
      </InstructorContainer>
      
      <Footer />
    </PageContainer>
  );
};

export default InstructorPage;